import React from "react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { selectors } from "@ReduxStore/reducers";
import { ROUTES } from "@Src/constants";
import {
  campaignDataFilterUtils,
  jsonCampaignFilenameMapping,
} from "@Src/utils/campaignData.utils";

const { REACT_APP_STATIC_URL } = process.env;

/**
 * useCampaignData Hook
 * @param {string} campaignType The campaign channel placement name for the data to be fetched.
 * @returns Campaign Data Object from JSONp file hosted on S3.
 */
export function useCampaignData(campaignType) {
  const [campaignData, setCampaignData] = React.useState(null);
  const isPreviewProgram = useSelector(selectors.getIsPreviewProgram);
  const travelEnabled = useSelector(selectors.getTravelEnabled);
  const travelTabsConfig = useSelector(selectors.getTravelTabsConfig);
  const storeMappings = useSelector(selectors.getStoreMappings);
  const attractions = useSelector(selectors.getAttractions);
  const navigate = useNavigate();

  const handleCampaignClick = (linkObj, clickHandler, triggerEl = null) => {
    let route = null;
    let routeProps = {};
    if (linkObj.linkType === "attraction") {
      const storeMapMatch = storeMappings.find((store) => store.attraction_id === linkObj.linkKey);
      if (storeMapMatch) {
        route = ROUTES.PARKS_PATH;
        routeProps = { ...storeMapMatch };
      }
    } else if (linkObj.linkType === "retailer") {
      const storeMapMatch = storeMappings.find((store) => store.retailer_id === linkObj.linkKey);
      if (storeMapMatch) {
        route = ROUTES.RETAILER_PATH;
        routeProps = { ...storeMapMatch };
      }
    } else if (linkObj.linkType === "offer") {
      const route = `${ROUTES.DIRECTOR_PATH}?view=offer&offer_key=${linkObj.linkKey}`;
      navigate(route);
    } else if (linkObj.linkType === "internal") {
      const route = `${ROUTES.DIRECTOR_PATH}?${linkObj.linkKey}`;
      navigate(route);
    } else if (clickHandler) {
      const data = {};
      if (linkObj.linkType === "brand") {
        data.store_key = linkObj.linkKey;
      } else if (linkObj.linkType === "external") {
        data.external_url = linkObj.linkKey;
      }
      clickHandler(data, triggerEl);
    }
    if (route) {
      navigate(route, {
        state: routeProps,
      });
    }
  };

  const checkCampaignItem = React.useCallback(
    async (item) => {
      let removeItem = false;
      const { status, dates, subscriptionRequirements, imageAssets } = item;

      if (status) {
        // check the status of the item
        removeItem = !campaignDataFilterUtils.hasVisibleStatus(status, isPreviewProgram);
      }

      if (!removeItem && dates) {
        // check if today is within the items run dates
        removeItem = !campaignDataFilterUtils.datesInRange(dates);
      }

      if (!removeItem && subscriptionRequirements) {
        const { brand, offer, travelTab, attraction, retailer } = subscriptionRequirements;
        if (!removeItem && brand) {
          // check if the item has a related brand in the subscription
          await campaignDataFilterUtils.hasAvailableBrand(brand).then((result) => {
            removeItem = !result;
            return;
          });
        }

        if (!removeItem && offer) {
          // check if the item has a related offer in the subscription
          await campaignDataFilterUtils.hasAvailableOffer(offer).then((result) => {
            removeItem = !result;
            return;
          });
        }

        if (!removeItem && travelTab) {
          // check if the item is available based on travel tab config
          removeItem = !campaignDataFilterUtils.hasAvailableTravelTab(
            travelTab,
            travelEnabled,
            travelTabsConfig,
          );
        }

        if (!removeItem && attraction) {
          // check if the item has a related attraction in the subscription
          removeItem = !campaignDataFilterUtils.hasAvailablePark(
            attraction,
            attractions,
            travelTabsConfig.theme_park_tab_enabled,
          );
        }

        if (!removeItem && retailer) {
          // check if the item has a related retailer in the subscription
          removeItem = !campaignDataFilterUtils.hasAvailableRetailer(retailer, attractions);
        }
      }

      if (!removeItem && imageAssets) {
        for (let j = 0; j < imageAssets.length; j++) {
          imageAssets[j].fullurl = `${REACT_APP_STATIC_URL}/campaigns_v2${imageAssets[j].url}`;
        }
      }

      return removeItem ? null : item;
    },
    [attractions, travelEnabled, travelTabsConfig, isPreviewProgram],
  );

  const handleCampaignData = React.useCallback(
    async (data) => {
      let doSetData = true;
      try {
        const { channelId, channelStatus, channelDates, content } = data;
        if (!channelId) {
          // no id should mean an empty campaign, or an error
          doSetData = false;
          console.error("Invalid campaign data: ", data);
        }
        if (doSetData && channelStatus) {
          // check the channel status
          doSetData = campaignDataFilterUtils.hasVisibleStatus(channelStatus, isPreviewProgram);
        }
        if (doSetData && channelDates) {
          // check if today is within the campaign channels run dates
          doSetData = campaignDataFilterUtils.datesInRange(channelDates);
        }
        if (doSetData && (!content || content.length === 0)) {
          // if no content, no need to set data
          doSetData = false;
        }
        if (!doSetData) {
          setCampaignData(null);
          return;
        }

        // run through the content array and scrub each item
        // based on status, dates and subscription
        let tiles = [];
        const scrubCampaignContent = async () => {
          for (let item of data.content) {
            const tile = await checkCampaignItem(item);
            if (tile) {
              // add the item to the tiles array
              tiles.push(tile);
            }
          }
          return;
        };
        await scrubCampaignContent();

        data.content = tiles;
        setCampaignData(data);
      } catch (error) {
        console.error("Error parsing campaign data: ", error);
        setCampaignData(null);
      }
    },
    [isPreviewProgram, checkCampaignItem],
  );

  React.useEffect(() => {
    if (attractions.length) {
      const fileName = jsonCampaignFilenameMapping[campaignType];
      if (!fileName) {
        console.error("Invalid campaign type: ", campaignType);
        return;
      }
      const scriptId = `${fileName}-script`;
      const dataId = `${fileName}_data`;
      const getData = () => {
        if (window[dataId]) {
          handleCampaignData(window[dataId]);
        } else {
          setTimeout(getData, 200);
        }
      };
      if (!document.getElementById(scriptId)) {
        const script = document.createElement("script");
        script.id = scriptId;
        script.src = `${REACT_APP_STATIC_URL}/campaigns_v2/${fileName}.js`;
        script.async = true;
        document.head.appendChild(script);
        getData();
      } else {
        getData();
      }
    }
  }, [campaignType, attractions, handleCampaignData]);

  return { campaignData, handleCampaignClick };
}
